// frontend/src/offers/offerEngine.js
// Display copy of backend/services/offerEngine.js (the server is authoritative and re-prices
// every checkout). If you ever change a rule, change BOTH files.

// ─────────────────────────────────────────────────────────────────────────────
// VESHH-EON · Offer engine (BOGO core)
//
// ONE rule set, used by BOTH the backend (authoritative) and the frontend
// (display only). Pure functions: no I/O, no framework, no Date.now().
//
// Rule: among ELIGIBLE units in the cart, the cheapest floor(n / 2) units are
//       free, limited to `maxFreeItems` (0 = unlimited).
//         2 items  -> the cheaper one is free   (the higher price is paid)
//         3 items  -> the cheapest 1 is free
//         4 items  -> the cheapest 2 are free
//       Equal prices: either may be free (totals are identical); ordering is
//       deterministic so results never flicker.
// All money maths is done in integer paise to avoid float errors.
// ─────────────────────────────────────────────────────────────────────────────

const toPaise = (v) => {
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? Math.round(n * 100) : 0;
};

const lineKey = (l) =>
  `${String(l.productId)}|${l.size ? String(l.size) : ''}|${l.color ? String(l.color) : ''}`;

/**
 * @param {Array<{productId, size?, color?, quantity, price, eligible}>} lines
 * @param {{maxFreeItems?: number}} options  maxFreeItems: 0 / undefined = unlimited
 */
function computeBogo(lines, options = {}) {
  const cap = Math.floor(Number(options.maxFreeItems));
  const maxFree = Number.isFinite(cap) && cap > 0 ? cap : 0;

  let subtotalPaise = 0;
  let eligibleUnits = 0;
  const eligibleLines = [];

  for (const l of Array.isArray(lines) ? lines : []) {
    const qty = Math.floor(Number(l && l.quantity));
    if (!(qty > 0)) continue;
    const p = toPaise(l.price);
    subtotalPaise += p * qty;
    if (!l.eligible) continue;
    eligibleUnits += qty;
    eligibleLines.push({ key: lineKey(l), p, qty });
  }

  const pairs = Math.floor(eligibleUnits / 2);
  const freeCount = maxFree > 0 ? Math.min(pairs, maxFree) : pairs;
  const capped = maxFree > 0 && pairs > maxFree;

  // cheapest first; ties broken by key so the result is deterministic
  eligibleLines.sort((a, b) => a.p - b.p || (a.key < b.key ? -1 : a.key > b.key ? 1 : 0));

  let left = freeCount;
  let discountPaise = 0;
  const freeByKey = {};
  for (const l of eligibleLines) {
    if (left <= 0) break;
    const take = Math.min(l.qty, left);
    freeByKey[l.key] = (freeByKey[l.key] || 0) + take;
    discountPaise += take * l.p;
    left -= take;
  }

  return {
    subtotal: subtotalPaise / 100,
    discount: discountPaise / 100,
    total: (subtotalPaise - discountPaise) / 100,
    freeCount,
    freeByKey,
    eligibleUnits,
    capped,
    // 1 => "add one more eligible item to unlock another free one"
    unitsToNextFree: eligibleUnits % 2 === 1 && !(maxFree > 0 && freeCount >= maxFree) ? 1 : 0,
  };
}

// ── Frontend-only helpers ────────────────────────────────────────────────────
// `eligible` comes from GET /api/offers/active:  { all: true }  or  { productIds: [...] }
const isEligible = (eligible, productId) => {
  if (!eligible) return false;
  if (eligible.all) return true;
  return Array.isArray(eligible.productIds) && eligible.productIds.includes(String(productId));
};

/** Builds everything the UI needs from the cart lines + the currently active offer. */
function buildQuote(products, offer) {
  const list = Array.isArray(products) ? products : [];
  const lines = list.map((p) => ({
    productId: p && p.productId,
    size: p && p.size,
    color: p && p.color,
    quantity: Number(p && p.quantity) || 0,
    price: Number(p && p.price) || 0,
    eligible: !!offer && isEligible(offer.eligible, p && p.productId),
  }));
  const r = computeBogo(lines, { maxFreeItems: offer ? offer.maxFreeItems : 0 });
  return {
    ...r,
    applied: !!offer && r.discount > 0,
    offer: offer || null,
    /** how many units of this cart line are free */
    freeQtyFor: (p) =>
      r.freeByKey[lineKey({ productId: p && p.productId, size: p && p.size, color: p && p.color })] || 0,
  };
}

export { computeBogo, lineKey, toPaise, isEligible, buildQuote };
